import React, { useState } from 'react';
import { useFormContext, type FieldErrors, type FieldValues, type Path } from 'react-hook-form';
import { cn } from '@/lib/cn';

export interface LedgerWizardStep<T extends FieldValues = FieldValues> {
  key: string;
  label: string;
  content: React.ReactNode;
  /** RHF field names this step owns — linear Next validates exactly these. */
  fields?: Path<T>[];
  /** Hidden in linear (create) mode; rendered in free (edit) mode. */
  editOnly?: boolean;
}

interface LedgerWizardProps<T extends FieldValues = FieldValues> {
  steps: LedgerWizardStep<T>[];
  mode: 'linear' | 'free';
  formId: string;
  submitLabel: string;
  isSubmitting?: boolean;
  onCancel: () => void;
}

interface LedgerWizardResult<T extends FieldValues = FieldValues> {
  rail: React.ReactNode;
  body: React.ReactNode;
  footer: React.ReactNode;
  activeKey: string;
  /** Rewinds to step 0 and relocks the linear rail. Dialog owners that stay
   * permanently mounted (the house pattern — see CompanyFormDialog reseeding
   * RHF via useEffect on [entity, open]) must call this in that same reseed
   * effect, or a closed-then-reopened create wizard resumes mid-flight with
   * steps unlocked past where the user actually re-validated. */
  reset: () => void;
  /**
   * Jumps to the first visible step whose `fields` intersect the given RHF
   * errors, so a Save submitted while an earlier (now-unmounted) step is
   * invalid doesn't render its error into thin air. Field matching is by
   * top-level key only — a nested path like "address.city" is matched via
   * its root segment "address", not the full path — which is enough for the
   * flat, single-level field lists every wizard step declares today.
   * Wire it as `handleSubmit(onValid, (errors) => goToFirstInvalid(errors))`.
   */
  goToFirstInvalid: (errors: FieldErrors<T>) => void;
}

// Mirrors RoleAssignmentDialog's footer buttons byte-for-byte so every
// LedgerDialog form — wizard or not — shares one footer vocabulary.
const cancelButtonClass =
  'cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60';
const submitButtonClass =
  'cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60';

export function useLedgerWizard<T extends FieldValues = FieldValues>({
  steps,
  mode,
  formId,
  submitLabel,
  isSubmitting = false,
  onCancel,
}: LedgerWizardProps<T>): LedgerWizardResult<T> {
  // editOnly steps only exist for the edit (free) surface; create (linear)
  // never shows them at all — not even as a disabled/skippable rail entry.
  const visible = mode === 'linear' ? steps.filter((step) => !step.editOnly) : steps;
  const [active, setActive] = useState(0);
  const [maxVisited, setMaxVisited] = useState(0);
  const { trigger } = useFormContext<T>();

  // Defensive clamp: if `steps` shrinks out from under an already-advanced
  // wizard (e.g. a mode flip), don't index off the end of `visible`. Every
  // navigation helper below operates on this clamped index, not the raw
  // `active` state, so Back/Next/rail clicks stay consistent with what's
  // actually on screen after such a shrink.
  const safeActive = Math.min(active, Math.max(visible.length - 1, 0));
  const isLastStep = safeActive === visible.length - 1;

  const goNext = async () => {
    const fields = visible[safeActive]?.fields;
    const ok = fields?.length ? await trigger(fields) : true;
    if (!ok) return;
    const next = Math.min(safeActive + 1, visible.length - 1);
    setActive(next);
    setMaxVisited((current) => Math.max(current, next));
  };

  const goBack = () => setActive(Math.max(safeActive - 1, 0));

  const goToStep = (index: number) => {
    // free mode: every step is always reachable. linear mode: only steps the
    // user has already visited (or the current one) — future steps must be
    // earned by passing Next's validation, not jumped to from the rail.
    if (mode === 'linear' && index > maxVisited) return;
    setActive(index);
    setMaxVisited((current) => Math.max(current, index));
  };

  const reset = () => {
    setActive(0);
    setMaxVisited(0);
  };

  const goToFirstInvalid = (errors: FieldErrors<T>) => {
    const rootErrorKeys = new Set(Object.keys(errors));
    const index = visible.findIndex((step) =>
      step.fields?.some((field) => rootErrorKeys.has(String(field).split('.')[0])),
    );
    if (index === -1) return;
    setActive(index);
    setMaxVisited((current) => Math.max(current, index));
  };

  const rail = (
    <ol
      aria-label="Steps"
      className="mb-4 flex gap-4 border-b border-ink pb-2 font-mono text-scale-0"
    >
      {visible.map((step, index) => {
        const isActive = index === safeActive;
        const reachable = mode === 'free' || index <= maxVisited;
        return (
          <li key={step.key}>
            <button
              type="button"
              aria-current={isActive ? 'step' : undefined}
              disabled={!reachable || isSubmitting}
              onClick={() => goToStep(index)}
              className={cn(
                'cursor-pointer disabled:cursor-default disabled:opacity-40',
                isActive ? 'text-ink underline' : 'text-ink-muted hover:text-ink',
              )}
            >
              {step.label}
            </button>
          </li>
        );
      })}
    </ol>
  );

  const submitButton = (
    <button type="submit" form={formId} disabled={isSubmitting} className={submitButtonClass}>
      {submitLabel}
    </button>
  );

  const footer =
    mode === 'free' ? (
      <>
        <button type="button" onClick={onCancel} disabled={isSubmitting} className={cancelButtonClass}>
          Cancel
        </button>
        {submitButton}
      </>
    ) : (
      <>
        <button type="button" onClick={onCancel} disabled={isSubmitting} className={cancelButtonClass}>
          Cancel
        </button>
        {safeActive > 0 && (
          <button type="button" onClick={goBack} disabled={isSubmitting} className={cancelButtonClass}>
            Back
          </button>
        )}
        {isLastStep ? (
          submitButton
        ) : (
          <button type="button" onClick={goNext} disabled={isSubmitting} className={submitButtonClass}>
            Next
          </button>
        )}
      </>
    );

  return {
    rail,
    body: visible[safeActive]?.content,
    footer,
    activeKey: visible[safeActive]?.key ?? '',
    reset,
    goToFirstInvalid,
  };
}
